import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { TOPIC_CONTENT, TOPICS } from "../data/content";
import { ArrowLeft, BookmarkSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TopicsList() {
  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#0033A0]">Topics</p>
      <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter text-slate-900 mt-3">
        All benefit topics
      </h1>
      <p className="text-lg text-slate-700 mt-5 max-w-2xl leading-relaxed">
        Pick a topic for plain-language explainers, examples, and links to official sources.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
        {TOPICS.filter((t) => TOPIC_CONTENT[t.slug]).map((t, i) => (
          <Link
            key={t.slug}
            to={`/topics/${t.slug}`}
            data-testid={`topics-list-${t.slug}`}
            className="bg-white border border-slate-200 rounded-xl p-6 hover:border-[#0033A0] hover:shadow-lg hover:-translate-y-0.5 transition-all fade-up"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <h3 className="font-display font-bold text-slate-900 text-lg">{t.title}</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.tagline}</p>
          </Link>
        ))}
      </div>
    </main>
  );
}

export function TopicDetail() {
  const { slug } = useParams();
  const data = TOPIC_CONTENT[slug];
  const { user } = useAuth();

  if (!data) return <Navigate to="/topics" replace />;

  const bookmark = async () => {
    if (!user) {
      toast("Log in to bookmark guides", { description: "Click 'Get started' in the top right." });
      return;
    }
    try {
      await axios.post(`${API}/bookmarks`, { slug, title: data.title });
      toast.success(`Saved "${data.title}" to your dashboard.`);
    } catch (e) {
      toast.error("Couldn't save bookmark");
    }
  };

  return (
    <main className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <Link to="/topics" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0033A0]" data-testid="back-to-topics">
        <ArrowLeft size={16} weight="bold" /> All topics
      </Link>

      <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#0033A0] mt-8">Topic</p>
      <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter text-slate-900 mt-3 leading-[1.05]">
        {data.title}
      </h1>
      <p className="text-lg text-slate-700 mt-5 leading-relaxed">{data.subtitle}</p>

      <div className="mt-6">
        <button
          onClick={bookmark}
          data-testid="topic-bookmark-btn"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#0033A0] hover:text-[#002277]"
        >
          <BookmarkSimple size={18} weight="bold" /> Save this guide
        </button>
      </div>

      <div className="mt-12 space-y-10">
        {data.sections.map((s, i) => (
          <section key={i} className="border-l-4 border-[#0033A0]/30 pl-6">
            <h2 className="font-display text-2xl font-bold text-slate-900">{s.h}</h2>
            <p className="text-slate-700 mt-3 leading-relaxed text-[15px]">{s.p}</p>
          </section>
        ))}
      </div>

      <div className="mt-16 bg-slate-50 border border-slate-200 rounded-xl p-6 md:p-8">
        <h3 className="font-display font-bold text-lg text-slate-900">Want personalized answers?</h3>
        <p className="text-slate-700 mt-2 text-sm leading-relaxed">
          Open the Boot Buddy chat in the corner and ask anything — eligibility, examples, or how a benefit applies to your situation.
        </p>
        <a
          href="https://www.va.gov"
          target="_blank"
          rel="noreferrer"
          data-testid="topic-official-link"
          className="mt-4 inline-flex items-center gap-2 text-[#0033A0] font-semibold hover:underline"
        >
          Official VA resources <ArrowSquareOut size={16} weight="bold" />
        </a>
      </div>
    </main>
  );
}
